import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Flag } from "lucide-react";

const REASONS = [
    { v: "spam_or_scam",           label: "Spam or scam" },
    { v: "inaccurate_info",        label: "Inaccurate information" },
    { v: "inappropriate_content",  label: "Inappropriate content" },
    { v: "duplicate_pin",          label: "Duplicate pin" },
    { v: "harassment",             label: "Harassment / abusive" },
    { v: "safety_concern",         label: "Safety concern" },
    { v: "other",                  label: "Something else" },
];

/**
 * Small "Report" button + confirmation dialog. Signed-out users are nudged to sign in.
 * Owners of the target should not see this — pass `hideForOwner` when the caller knows.
 */
export default function ReportButton({ targetType, targetId, targetLabel, hideForOwner, variant = "ghost" }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    if (hideForOwner) return null;

    const submit = async () => {
        if (!user) { toast.error("Sign in to report content"); return; }
        if (!reason) { toast.error("Pick a reason"); return; }
        setBusy(true);
        try {
            await api.post("/reports", {
                target_type: targetType, target_id: targetId,
                reason, notes,
            });
            toast.success("Thanks — an admin will review this");
            setOpen(false); setReason(""); setNotes("");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid={`report-btn-${targetType}-${targetId}`}
                    className={
                        variant === "compact"
                            ? "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-red-700 transition-colors"
                            : "inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-700 transition-colors"
                    }
                    title="Report this"
                >
                    <Flag size={variant === "compact" ? 10 : 12} /> Report
                </button>
            </DialogTrigger>
            <DialogContent data-testid="report-dialog">
                <DialogHeader>
                    <DialogTitle>Report this {targetType}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                        Reporting <b>{targetLabel || targetId}</b>. Admins will review and take it down if needed.
                    </p>
                    <div>
                        <Label>Reason</Label>
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Pick a reason…" /></SelectTrigger>
                            <SelectContent>
                                {REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                            data-testid="report-notes" rows={3}
                            placeholder="Anything an admin should know" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !reason}
                        data-testid="report-submit"
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                        {busy ? "Submitting…" : "Send report"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
